"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";
import { createClient } from "@/lib/supabase/client";
import PWAInstallPrompt from "./components/PWAInstallPrompt";

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────
type SliderLayout = 1 | 2 | 3 | 4;

interface SliderConfig {
  layout: SliderLayout;
  images: string[][];
}

interface DriveImage {
  id: string;
  name: string;
  thumbUrl: string;
  fullUrl: string;
}

interface GalleryTopic {
  id: string;
  label: string;
  icon: string;
  sectionName: string;
  folderId: string | null;
  images: DriveImage[];
  loaded: boolean;
}

interface VideoItem {
  id: string;
  title: string;
  youtubeUrl: string;
  thumbnailUrl: string;
}

interface GallerySource {
  section_name: string;
  drive_folder_link: string;
  max_images: number;
  auto_slide_seconds: number;
}

// ─────────────────────────────────────────────
// CLIENT-SIDE IMAGE CACHE (5 min TTL)
// ─────────────────────────────────────────────
const imageCache = new Map<string, { images: DriveImage[]; ts: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

async function fetchDriveImages(folderId: string): Promise<DriveImage[]> {
  if (!folderId) return [];
  const cached = imageCache.get(folderId);
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.images;
  try {
    const res = await fetch(`/api/drive/images?folderId=${encodeURIComponent(folderId)}`);
    if (!res.ok) return [];
    const data = await res.json();
    const images: DriveImage[] = data.images ?? [];
    imageCache.set(folderId, { images, ts: Date.now() });
    return images;
  } catch {
    return [];
  }
}

function extractFolderId(link: string): string | null {
  if (!link) return null;
  const folderMatch = link.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  if (folderMatch) return folderMatch[1];
  const idMatch = link.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (idMatch) return idMatch[1];
  if (/^[a-zA-Z0-9_-]{10,}$/.test(link.trim())) return link.trim();
  return null;
}

// ─────────────────────────────────────────────
// MOCK DATA (fallback only)
// ─────────────────────────────────────────────
const SLIDER_CONFIG: SliderConfig = {
  layout: 2,
  images: [[], [], [], []],
};

const GALLERY_TOPIC_DEFS: { id: string; label: string; icon: string; sectionName: string }[] = [
  {
    id: "correspondent", label: "Our Correspondent", icon: "UserCircleIcon", sectionName: "correspondent",
  },
  {
    id: "principal", label: "Our Principal", icon: "AcademicCapIcon", sectionName: "principal",
  },
  {
    id: "campus", label: "Our Campus", icon: "BuildingLibraryIcon", sectionName: "campus",
  },
  {
    id: "teachers", label: "Our Teachers", icon: "UsersIcon", sectionName: "teachers",
  },
  {
    id: "admins", label: "Our Admin Staff", icon: "BriefcaseIcon", sectionName: "admins",
  },
  {
    id: "supporting_staff", label: "Supporting Staff", icon: "HeartIcon", sectionName: "supporting_staff",
  },
  {
    id: "sports_events", label: "Sports Events", icon: "TrophyIcon", sectionName: "sports_events",
  },
  {
    id: "cultural_events", label: "Cultural Events", icon: "MusicalNoteIcon", sectionName: "cultural_events",
  },
  {
    id: "tours", label: "Tours", icon: "MapPinIcon", sectionName: "tours",
  },
  {
    id: "toppers", label: "Toppers", icon: "StarIcon", sectionName: "toppers",
  },
  {
    id: "hall_of_fame", label: "Hall of Fame", icon: "FireIcon", sectionName: "hall_of_fame",
  },
];

// Helper: extract YouTube ID from any YouTube URL format
function extractYouTubeId(url: string): string {
  if (!url) return "";
  // Handles: youtu.be/ID, ?v=ID, &v=ID, /embed/ID, /v/ID, /shorts/ID
  return (
    url.match(/(?:youtu\.be\/|[?&]v=|\/embed\/|\/v\/|\/shorts\/)([A-Za-z0-9_-]{11})/)?.[1] ?? ""
  );
}

// Helper: map DB row to VideoItem
function mapVideoRow(row: { id: string; title: string; youtube_url: string }): VideoItem {
  const ytId = extractYouTubeId(row.youtube_url);
  return {
    id: row.id,
    title: row.title,
    youtubeUrl: row.youtube_url,
    thumbnailUrl: ytId
      ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`
      : "/assets/images/no_image.png",
  };
}

// ─────────────────────────────────────────────
// HERO SLIDER COMPONENT
// ─────────────────────────────────────────────
const HeroSlider: React.FC<{ config: SliderConfig }> = ({ config }) => {
  const [layout, setLayout] = useState<SliderLayout>(config.layout);
  const [images, setImages] = useState<string[][]>([]);
  const [loaded, setLoaded] = useState(false);
  const [indices, setIndices] = useState<number[]>(Array(config.layout).fill(0));
  const [autoSlideSeconds, setAutoSlideSeconds] = useState(3);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const imagesRef = useRef<string[][]>([]);

  // Keep ref in sync with state so interval always has latest images
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(() => {
    const loadHeroImages = async () => {
      const supabase = createClient();

      // First try gallery_sources for Drive link
      const { data: sourceData } = await supabase
        .from("gallery_sources")
        .select("drive_folder_link, auto_slide_seconds")
        .eq("section_name", "hero_slider")
        .single();

      if (sourceData?.drive_folder_link) {
        const folderId = extractFolderId(sourceData.drive_folder_link);
        if (folderId) {
          const driveImages = await fetchDriveImages(folderId);
          if (driveImages.length > 0) {
            const slideSeconds = sourceData.auto_slide_seconds ?? 3;
            setAutoSlideSeconds(slideSeconds);
            // Distribute images across layout slots
            const dbLayout = Math.min(config.layout, 4) as SliderLayout;
            const perSlot = Math.ceil(driveImages.length / dbLayout);
            const slotImages: string[][] = Array(dbLayout).fill(null).map((_, i) =>
              driveImages.slice(i * perSlot, (i + 1) * perSlot).map((img) => img.thumbUrl)
            );
            setLayout(dbLayout);
            setImages(slotImages);
            setIndices(Array(dbLayout).fill(0));
            setLoaded(true);
            return;
          }
        }
      }

      // Fall back to hero_images DB table
      const { data, error } = await supabase
        .from("hero_images")
        .select("src, slot, sort_order")
        .order("slot", { ascending: true })
        .order("sort_order", { ascending: true });

      if (error || !data || data.length === 0) {
        setLayout(config.layout);
        setImages(config.images.length > 0 ? config.images : Array(config.layout).fill([]));
        setIndices(Array(config.layout).fill(0));
        setLoaded(true);
        return;
      }

      const maxSlot = Math.max(...data.map((r: { slot: number }) => r.slot));
      const dbLayout = Math.min(Math.max(maxSlot + 1, 1), 4) as SliderLayout;
      const grouped: string[][] = Array(dbLayout).fill(null).map(() => []);
      data.forEach((row: { src: string; slot: number }) => {
        if (row.slot < dbLayout) grouped[row.slot].push(row.src);
      });
      const finalImages = grouped.map((slotImgs, i) =>
        slotImgs.length > 0 ? slotImgs : (config.images[i] ?? [])
      );
      setLayout(dbLayout);
      setImages(finalImages);
      setIndices(Array(dbLayout).fill(0));
      setLoaded(true);
    };
    loadHeroImages();
  }, [config.layout, config.images]);

  // Real-time: re-fetch if hero_slider source changes
  useEffect(() => {
    const supabase = createClient();
    const channel = supabase
      .channel("hero_slider_realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "gallery_sources", filter: "section_name=eq.hero_slider" },
        async (payload) => {
          const newLink = (payload.new as GallerySource)?.drive_folder_link;
          const newSlideSeconds = (payload.new as GallerySource)?.auto_slide_seconds ?? 3;
          if (newLink) {
            const folderId = extractFolderId(newLink);
            if (folderId) {
              imageCache.delete(folderId);
              const driveImages = await fetchDriveImages(folderId);
              if (driveImages.length > 0) {
                setAutoSlideSeconds(newSlideSeconds);
                const dbLayout = Math.min(config.layout, 4) as SliderLayout;
                const perSlot = Math.ceil(driveImages.length / dbLayout);
                const slotImages: string[][] = Array(dbLayout).fill(null).map((_, i) =>
                  driveImages.slice(i * perSlot, (i + 1) * perSlot).map((img) => img.thumbUrl)
                );
                setLayout(dbLayout);
                setImages(slotImages);
                setIndices(Array(dbLayout).fill(0));
              }
            }
          }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [config.layout]);

  const tick = useCallback(() => {
    setIndices((prev) =>
      prev.map((idx, slot) => {
        const pool = imagesRef.current[slot] ?? [];
        return pool.length > 1 ? (idx + 1) % pool.length : 0;
      })
    );
  }, []);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (loaded && images.some((slot) => slot.length > 1)) {
      timerRef.current = setInterval(tick, autoSlideSeconds * 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [tick, autoSlideSeconds, loaded, images]);

  const getGridClass = () => {
    switch (layout) {
      case 1: return "grid-cols-1";
      case 2: return "grid-cols-2";
      case 3: return "grid-cols-3";
      case 4: return "grid-cols-2 grid-rows-2";
      default: return "grid-cols-1";
    }
  };

  const slotCount = Math.min(layout, Math.max(images.length, 1));

  if (!loaded) {
    return (
      <section id="home" className="relative w-full h-[100svh] min-h-[500px] overflow-hidden">
        <div className="w-full h-full bg-gradient-to-br from-[#060e1e] via-[#0d1a30] to-[#e53e3e]/20 animate-pulse" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-10 pointer-events-none">
          <div className="h-8 w-64 bg-[#e53e3e]/20 rounded-full mb-4 animate-pulse" />
          <div className="h-16 w-96 bg-[#e53e3e]/10 rounded-2xl mb-4 animate-pulse" />
          <div className="h-6 w-48 bg-[#e53e3e]/10 rounded-full animate-pulse" />
        </div>
      </section>
    );
  }

  return (
    <section id="home" className="relative w-full h-[100svh] min-h-[500px] overflow-hidden">
      <div className={`grid ${getGridClass()} w-full h-full`}>
        {Array.from({ length: slotCount }).map((_, slot) => {
          const pool = images[slot] ?? [];
          const src = pool[indices[slot]] ?? pool[0];
          return (
            <div key={slot} className="relative overflow-hidden">
              {src && (
                <AppImage
                  key={src}
                  src={src}
                  alt={`School campus view ${slot + 1}`}
                  fill
                  className="object-cover transition-transform duration-[8000ms] ease-linear scale-105 hover:scale-100"
                  priority={slot === 0}
                  unoptimized
                />
              )}
              <div className="absolute inset-0 hero-overlay" />
            </div>
          );
        })}
      </div>

      {/* Overlay Text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 sm:px-6 z-10 pointer-events-none pb-16 sm:pb-0">
        <div className="accent-badge px-3 sm:px-4 py-1 sm:py-1.5 rounded-full mb-4 sm:mb-6 animate-fadeInUp text-[10px] sm:text-[11px]">
          Estd. 2003 · State Board · English Medium
        </div>
        <h1 className="font-heading font-800 text-white text-2xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight mb-2 sm:mb-3 animate-fadeInUp delay-100 drop-shadow-2xl">
          Royal Schools
        </h1>
        <p className="font-heading font-600 text-[#e53e3e] text-base sm:text-xl md:text-2xl mb-3 sm:mb-4 animate-fadeInUp delay-200 tracking-wide">
          Matriculation • Nursery • CBSE
        </p>
        <p className="font-body text-white/85 text-sm sm:text-base sm:text-lg mb-6 sm:mb-8 animate-fadeInUp delay-300 tracking-[0.08em] sm:tracking-[0.12em] uppercase">
          Research · Self Confidence · Humility
        </p>
        <div className="flex flex-col xs:flex-row gap-3 sm:gap-4 animate-fadeInUp delay-400 pointer-events-auto w-full max-w-xs sm:max-w-none sm:w-auto">
          <button
            onClick={() => { const el = document.getElementById("about"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}
            className="px-6 sm:px-8 py-3 sm:py-3.5 bg-gradient-to-r from-[#e53e3e] to-[#c53030] text-white rounded-full font-heading font-700 text-sm hover:from-[#fc8181] hover:to-[#e53e3e] transition-all duration-300 shadow-lg hover:-translate-y-1">
            Discover Our School
          </button>
          <button
            onClick={() => { const el = document.getElementById("contact"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}
            className="px-6 sm:px-8 py-3 sm:py-3.5 border-2 border-[#e53e3e]/60 text-white rounded-full font-heading font-700 text-sm hover:bg-[#e53e3e] hover:text-white hover:border-[#e53e3e] transition-all duration-300 hover:-translate-y-1">
            Contact Us
          </button>
        </div>
        <div className="absolute bottom-6 sm:bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-float">
          <span className="text-white/50 text-[10px] tracking-[0.4em] uppercase font-heading">Scroll</span>
          <div className="w-px h-8 sm:h-10 bg-gradient-to-b from-white/40 to-transparent" />
        </div>
      </div>

      {/* Slide Dots */}
      <div className="absolute bottom-3 sm:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
        {(images[0] ?? []).map((_, i) => (
          <button
            key={i}
            onClick={() => setIndices((prev) => prev.map((_, s) => i % (images[s]?.length || 1)))}
            className={`slider-dot ${indices[0] === i ? "active" : ""}`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────
// ABOUT SECTION
// ─────────────────────────────────────────────
const AboutSection: React.FC = () => {
  const stats = [
    { value: "1997", label: "Year Founded" },
    { value: "KG–XII", label: "Classes Offered" },
    { value: "20:1", label: "Student–Teacher Ratio" },
    { value: "100%", label: "English Medium" },
  ];

  const [aboutImage, setAboutImage] = useState("https://images.unsplash.com/photo-1611581719398-08fe2eb020c7");

  useEffect(() => {
    const fetchAboutImage = async () => {
      const supabase = createClient();
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("id", "about_us_image")
        .single();
      if (!error && data?.value) {
        setAboutImage(data.value);
      }
    };
    fetchAboutImage();
  }, []);

  return (
    <section id="about" className="py-20 lg:py-28 bg-[#060e1e]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-4 reveal">
          <div className="h-px w-10 bg-[#e53e3e]" />
          <span className="accent-badge px-3 py-1 rounded-full">About Us</span>
        </div>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <h2 className="font-heading font-800 text-3xl sm:text-4xl md:text-5xl text-[#F0E6CC] leading-tight mb-6 reveal">
              Building Character,<br />
              <span className="text-[#e53e3e]">Shaping Futures</span>
            </h2>
            <p className="font-body text-[#8A9BB8] text-lg leading-relaxed mb-6 reveal">
              Founded in 1997, Royal Schools Matriculation School has been a beacon of quality education in Ayanambakkam, Chennai. Our school follows the Tamil Nadu State Board curriculum with English as the medium of instruction.
            </p>
            <p className="font-body text-[#8A9BB8] text-base leading-relaxed mb-8 reveal">
              Our main aim is to help and train children according to their own abilities and potentialities — developing their talent, character, and personalities through individual attention. We believe every child is unique and deserves the opportunity to flourish.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 reveal">
              {["Research", "Self Confidence", "Humility"].map((val) => (
                <div key={val} className="flex items-center gap-2 p-3 bg-[#0d1a30] rounded-xl border border-[#e53e3e]/20">
                  <div className="w-2 h-2 rounded-full bg-[#e53e3e] flex-shrink-0" />
                  <span className="font-heading font-600 text-sm text-[#e53e3e]">{val}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="relative pb-10 lg:pb-0">
              <div className="relative rounded-3xl overflow-hidden shadow-primary-lg reveal">
                <AppImage
                  src={aboutImage}
                  alt="Students engaged in classroom learning at Royal Schools"
                  width={600}
                  height={420}
                  className="w-full object-cover"
                  unoptimized
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#060e1e]/80 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="font-heading font-700 text-[#e53e3e] text-lg">&quot;Excellence in Education&quot;</p>
                  <p className="font-body text-white/70 text-sm">Thakkolam – 631151</p>
                </div>
              </div>
              <div className="absolute -bottom-8 right-0 lg:-right-8 bg-[#0d1a30] rounded-2xl shadow-card-hover p-3 lg:p-4 grid grid-cols-2 gap-2 lg:gap-3 w-48 lg:w-56 reveal border border-[#e53e3e]/25">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="font-heading font-800 text-[#e53e3e] text-base lg:text-lg leading-none">{stat.value}</div>
                    <div className="font-body text-[#8A9BB8] text-[9px] lg:text-[10px] mt-0.5 leading-tight">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────
// ACADEMICS SECTION
// ─────────────────────────────────────────────
const AcademicsSection: React.FC = () => {
  const cards = [
    { icon: "BookOpenIcon", title: "Matriculation Education", desc: "Tamil Nadu State Board curriculum from KG to Class XII, delivered in English medium with rigorous academic standards and board exam preparation.", color: "from-[#0d1a30] to-[#0a1525]", iconBg: "bg-gradient-to-br from-[#e53e3e] to-[#c53030]", features: ["State Board Syllabus", "English Medium", "KG to XII"] },
    { icon: "LightBulbIcon", title: "Smart Learning", desc: "Technology-integrated classrooms with digital learning tools, interactive sessions, and modern teaching methodologies that make concepts come alive.", color: "from-[#0d1a30] to-[#0a1525]", iconBg: "bg-gradient-to-br from-[#fc8181] to-[#e53e3e]", features: ["Digital Classrooms", "Interactive Learning", "STEM Focus"] },
    { icon: "SparklesIcon", title: "Holistic Development", desc: "Co-curricular activities including dance, music, arts, sports, and recreational programs to enhance agility, confidence, and cooperative skills.", color: "from-[#0d1a30] to-[#0a1525]", iconBg: "bg-gradient-to-br from-[#e53e3e] to-[#9b2c2c]", features: ["Arts & Culture", "Sports Programs", "Life Skills"] },
  ];

  return (
    <section id="academics" className="py-20 lg:py-28 bg-[#0a1525]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-4 reveal">
            <div className="h-px w-10 bg-[#e53e3e]" />
            <span className="accent-badge px-3 py-1 rounded-full">Academics</span>
            <div className="h-px w-10 bg-[#e53e3e]" />
          </div>
          <h2 className="font-heading font-800 text-3xl sm:text-4xl md:text-5xl text-[#F0E6CC] mb-4 reveal">
            A Complete <span className="text-[#e53e3e]">Learning Journey</span>
          </h2>
          <p className="font-body text-[#8A9BB8] text-lg max-w-xl mx-auto reveal">
            From foundational kindergarten to board examinations, we nurture every student&apos;s potential through structured academics and vibrant co-curriculars.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {cards.map((card, i) => (
            <div key={card.title} className={`relative bg-gradient-to-br ${card.color} rounded-3xl p-8 border border-[#e53e3e]/20 card-hover reveal`} style={{ animationDelay: `${i * 0.1}s` }}>
              <div className={`w-14 h-14 ${card.iconBg} rounded-2xl flex items-center justify-center mb-6 shadow-primary-sm`}>
                <Icon name={card.icon as Parameters<typeof Icon>[0]["name"]} size={26} className="text-white" variant="outline" />
              </div>
              <h3 className="font-heading font-700 text-xl text-[#F0E6CC] mb-3">{card.title}</h3>
              <p className="font-body text-[#8A9BB8] text-sm leading-relaxed mb-6">{card.desc}</p>
              <div className="flex flex-wrap gap-2">
                {card.features.map((f) => (
                  <span key={f} className="text-[11px] font-heading font-600 px-3 py-1 bg-[#060e1e]/60 text-[#e53e3e] rounded-full border border-[#e53e3e]/25">{f}</span>
                ))}
              </div>
              <div className="absolute top-4 right-4 w-20 h-20 rounded-full bg-[#e53e3e]/5 -z-0" />
            </div>
          ))}
        </div>
        <div className="mt-12 bg-gradient-to-r from-[#e53e3e] to-[#c53030] rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 reveal">
          <div>
            <h3 className="font-heading font-700 text-white text-xl md:text-2xl mb-2">Admissions Open for 2025–26</h3>
            <p className="font-body text-white/70 text-sm">Limited seats available · Apply early · All classes KG to XII</p>
          </div>
          <button
            onClick={() => { const el = document.getElementById("contact"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}
            className="flex-shrink-0 px-8 py-3.5 bg-[#060e1e] text-[#e53e3e] rounded-full font-heading font-700 text-sm hover:bg-[#0d1a30] transition-all duration-300 shadow-lg hover:-translate-y-1">
            Enquire Now
          </button>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────
// GALLERY SECTION (Drive-powered)
// ─────────────────────────────────────────────
const GallerySection: React.FC = () => {
  const [topics, setTopics] = useState<GalleryTopic[]>([]);
  const [activeTopic, setActiveTopic] = useState<string | null>(null);
  const [autoIndex, setAutoIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState<{ src: string; alt: string; images: { src: string; alt: string }[]; index: number } | null>(null);
  const [topicLoading, setTopicLoading] = useState(false);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Load gallery topics and images from Supabase on mount
  useEffect(() => {
    const loadGallery = async () => {
      const supabase = createClient();

      // Load gallery_sources (Drive folder links per section)
      const { data: sourcesData } = await supabase
        .from("gallery_sources")
        .select("section_name, drive_folder_link, max_images");

      const sourceMap: Record<string, { folderId: string | null; maxImages: number }> = {};
      (sourcesData ?? []).forEach((s: { section_name: string; drive_folder_link: string; max_images: number }) => {
        sourceMap[s.section_name] = {
          folderId: extractFolderId(s.drive_folder_link),
          maxImages: s.max_images ?? 50,
        };
      });

      // Load topics from gallery_topics table
      const { data: topicData, error: topicError } = await supabase
        .from("gallery_topics")
        .select("id, label, sort_order");

      // Load images from gallery_images table
      const { data: imageData } = await supabase
        .from("gallery_images")
        .select("id, topic_id, src, alt, sort_order");

      if (topicError || !topicData || topicData.length === 0) {
        // Fallback: use GALLERY_TOPIC_DEFS — check Drive links first
        const fallbackTopics: GalleryTopic[] = await Promise.all(
          GALLERY_TOPIC_DEFS.map(async (def) => {
            const source = sourceMap[def.sectionName];
            if (source?.folderId) {
              const driveImages = await fetchDriveImages(source.folderId);
              if (driveImages.length > 0) {
                return {
                  id: def.id,
                  label: def.label,
                  icon: def.icon,
                  sectionName: def.sectionName,
                  folderId: source.folderId,
                  images: driveImages.slice(0, source.maxImages),
                  loaded: true,
                };
              }
            }
            return {
              id: def.id,
              label: def.label,
              icon: def.icon,
              sectionName: def.sectionName,
              folderId: null,
              images: [],
              loaded: true,
            };
          })
        );
        setTopics(fallbackTopics);
        setGalleryLoading(false);
        return;
      }

      // Map DB topics to GalleryTopic shape — Drive link takes priority over uploaded images
      const dbTopics: GalleryTopic[] = await Promise.all(
        topicData.map(async (t: { id: string; label: string; sort_order: number }) => {
          const def = GALLERY_TOPIC_DEFS.find((d) => d.id === t.id || d.sectionName === t.id);
          const sectionName = def?.sectionName ?? t.id;
          const source = sourceMap[sectionName] ?? sourceMap[t.id];

          // If Drive folder link exists, fetch from Drive
          if (source?.folderId) {
            try {
              const driveImages = await fetchDriveImages(source.folderId);
              if (driveImages.length > 0) {
                return {
                  id: t.id,
                  label: t.label,
                  icon: def?.icon ?? "PhotoIcon",
                  sectionName,
                  folderId: source.folderId,
                  images: driveImages.slice(0, source.maxImages),
                  loaded: true,
                };
              }
            } catch {
              // fall through to uploaded images
            }
          }

          // Fall back to uploaded images from gallery_images table
          const topicImages = (imageData ?? [])
            .filter((img: { topic_id: string }) => img.topic_id === t.id)
            .map((img: { id: string; src: string; alt: string }) => ({
              id: img.id,
              name: img.alt || t.label,
              thumbUrl: img.src,
              fullUrl: img.src,
            }));

          return {
            id: t.id,
            label: t.label,
            icon: def?.icon ?? "PhotoIcon",
            sectionName,
            folderId: null,
            images: topicImages,
            loaded: true,
          };
        })
      );

      setTopics(dbTopics);
      setGalleryLoading(false);
    };
    loadGallery();
  }, []);

  // Real-time: re-fetch when gallery_topics or gallery_images change
  useEffect(() => {
    const supabase = createClient();
    const channel = supabase
      .channel("gallery_db_realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "gallery_topics" }, async () => {
        // Re-fetch all topics and images
        const { data, error } = await supabase
          .from("gallery_topics")
          .select("id, label, sort_order")
          .order("sort_order", { ascending: true });
        if (error) {
          console.error("[GalleryRealtime] error:", error.message);
        }
        if (data !== null) {
          const updated: GalleryTopic[] = data.map(
            (t: { id: string; label: string; sort_order: number }) => {
              const def = GALLERY_TOPIC_DEFS.find((d) => d.id === t.id || d.sectionName === t.id);
              return {
                id: t.id,
                label: t.label,
                icon: def?.icon ?? "PhotoIcon",
                sectionName: def?.sectionName ?? t.id,
                folderId: null,
                images: [],
                loaded: true,
              };
            }
          );
          setTopics(updated);
        }
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "gallery_images" }, async () => {
        const { data, error } = await supabase
          .from("gallery_images")
          .select("id, topic_id, src, alt, sort_order")
          .order("sort_order", { ascending: true });
        if (error) {
          console.error("[GalleryRealtime] error:", error.message);
        }
        if (data !== null) {
          setTopics((prev) =>
            prev.map((t) => ({
              ...t,
              images: data
                .filter((img: { topic_id: string }) => img.topic_id === t.id)
                .map((img: { id: string; src: string; alt: string }) => ({
                  id: img.id,
                  name: img.alt || t.label,
                  thumbUrl: img.src,
                  fullUrl: img.src,
                })),
            }))
          );
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // Real-time: re-fetch when any gallery source changes
  useEffect(() => {
    const supabase = createClient();
    const channel = supabase
      .channel("gallery_sources_realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "gallery_sources" },
        async (payload) => {
          const updated = payload.new as GallerySource | null;
          const deleted = payload.old as GallerySource | null;
          const sectionName = updated?.section_name ?? deleted?.section_name;

          // If hero_slider changed, skip here (handled by hero_slider_realtime channel)
          if (sectionName === "hero_slider") return;

          if (sectionName) {
            // UPDATE or INSERT: re-fetch images for the specific section
            const folderId = updated?.drive_folder_link ? extractFolderId(updated.drive_folder_link) : null;
            if (folderId) {
              imageCache.delete(folderId);
              const driveImages = await fetchDriveImages(folderId);
              setTopics((prev) =>
                prev.map((t) =>
                  t.sectionName === sectionName
                    ? { ...t, folderId, images: driveImages, loaded: true }
                    : t
                )
              );
            }
          }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // Auto-slide for "All" banner
  const allImages = topics.flatMap((t) => t.images.slice(0, 2).map((img) => ({ src: img.thumbUrl, alt: img.name, topicLabel: t.label })));

  useEffect(() => {
    if (allImages.length === 0) return;
    timerRef.current = setInterval(() => {
      setAutoIndex((i) => (i + 1) % allImages.length);
    }, 3000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [allImages.length]);

  // Load topic images when selected (for Drive-based topics that haven't loaded yet)
  const handleTopicSelect = useCallback(async (topicId: string | null) => {
    setActiveTopic(topicId);
    if (!topicId) return;
    const topic = topics.find((t) => t.id === topicId);
    if (!topic || topic.loaded) return;
    setTopicLoading(true);
    if (topic.folderId) {
      const driveImages = await fetchDriveImages(topic.folderId);
      setTopics((prev) => prev.map((t) => t.id === topicId ? { ...t, images: driveImages, loaded: true } : t));
    }
    setTopicLoading(false);
  }, [topics]);

  const activeTopicData = activeTopic ? topics.find((t) => t.id === activeTopic) : null;
  const displayImages = activeTopicData ? activeTopicData.images : [];

  const openFullscreen = (img: DriveImage, idx: number, imageList: DriveImage[]) => {
    setFullscreen({
      src: img.fullUrl,
      alt: img.name,
      images: imageList.map((i) => ({ src: i.fullUrl, alt: i.name })),
      index: idx,
    });
  };

  const navFullscreen = (dir: 1 | -1) => {
    if (!fullscreen) return;
    const newIdx = (fullscreen.index + dir + fullscreen.images.length) % fullscreen.images.length;
    const img = fullscreen.images[newIdx];
    setFullscreen({ ...fullscreen, src: img.src, alt: img.alt, index: newIdx });
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!fullscreen) return;
      if (e.key === "ArrowRight") navFullscreen(1);
      if (e.key === "ArrowLeft") navFullscreen(-1);
      if (e.key === "Escape") setFullscreen(null);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  });

  return (
    <section id="gallery" className="py-20 lg:py-28 bg-[#060e1e]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4 reveal">
            <div className="h-px w-10 bg-[#e53e3e]" />
            <span className="accent-badge px-3 py-1 rounded-full">Gallery</span>
            <div className="h-px w-10 bg-[#e53e3e]" />
          </div>
          <h2 className="font-heading font-800 text-3xl sm:text-4xl md:text-5xl text-[#F0E6CC] mb-4 reveal">
            Life at <span className="text-[#e53e3e]">Royal Schools</span>
          </h2>
          <p className="font-body text-[#8A9BB8] text-lg max-w-xl mx-auto reveal">
            Moments of learning, celebration, and achievement captured through our lens.
          </p>
        </div>

        {galleryLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-[#e53e3e]/30 border-t-[#e53e3e] rounded-full animate-spin" />
            <span className="ml-3 text-sm text-[#8A9BB8] font-body">Loading gallery...</span>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            {/* Sidebar — horizontal scrollable pills on mobile, vertical sticky on desktop */}
            <aside className="lg:w-56 flex-shrink-0">
              {/* Mobile: horizontal pill scroll */}
              <div className="lg:hidden flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
                <button
                  onClick={() => handleTopicSelect(null)}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-heading font-600 border transition-all ${!activeTopic ? "bg-[#e53e3e] border-[#e53e3e] text-white" : "bg-[#0d1a30] border-[#e53e3e]/20 text-white/80"}`}>
                  <Icon name="PhotoIcon" size={13} />All
                </button>
                {topics.map((topic) => (
                  <button
                    key={topic.id}
                    onClick={() => handleTopicSelect(topic.id)}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-heading font-600 border transition-all ${activeTopic === topic.id ? "bg-[#e53e3e] border-[#e53e3e] text-white" : "bg-[#0d1a30] border-[#e53e3e]/20 text-white/80"}`}>
                    <Icon name={topic.icon as Parameters<typeof Icon>[0]["name"]} size={12} />
                    {topic.label}
                  </button>
                ))}
              </div>
              {/* Desktop: vertical sticky sidebar */}
              <div className="hidden lg:block bg-[#0d1a30] rounded-2xl border border-[#e53e3e]/20 overflow-hidden sticky top-24">
                <div className="px-4 py-3 bg-gradient-to-r from-[#e53e3e] to-[#c53030]">
                  <p className="font-heading font-700 text-white text-sm tracking-wide">Topics</p>
                </div>
                <div className="p-2">
                  <button
                    onClick={() => handleTopicSelect(null)}
                    className={`topic-item w-full text-left px-3 py-2.5 rounded-lg text-sm font-heading font-600 transition-all ${!activeTopic ? "active" : "text-white/80"}`}>
                    <span className="flex items-center gap-2">
                      <Icon name="PhotoIcon" size={15} />All Photos
                    </span>
                  </button>
                  {topics.map((topic) => (
                    <button
                      key={topic.id}
                      onClick={() => handleTopicSelect(topic.id)}
                      className={`topic-item w-full text-left px-3 py-2.5 rounded-lg text-sm font-heading font-600 transition-all mt-0.5 ${activeTopic === topic.id ? "active" : "text-white/80"}`}>
                      <span className="flex items-center gap-2">
                        <Icon name={topic.icon as Parameters<typeof Icon>[0]["name"]} size={14} />
                        {topic.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {/* Gallery Grid */}
            <div className="flex-1 min-w-0">
              {/* All Photos: auto-rotating banner */}
              {!activeTopic && allImages.length > 0 && (
                <div className="relative rounded-2xl overflow-hidden h-40 sm:h-48 md:h-64 mb-4 sm:mb-6">
                  <AppImage
                    src={allImages[autoIndex]?.src ?? ""}
                    alt={allImages[autoIndex]?.alt ?? "School gallery"}
                    fill
                    className="object-cover transition-opacity duration-700"
                    unoptimized
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#e53e3e]/60 to-transparent" />
                  <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4">
                    <span className="font-heading font-600 text-white text-xs sm:text-sm">{allImages[autoIndex]?.topicLabel}</span>
                  </div>
                </div>
              )}

              {/* All Photos grid */}
              {!activeTopic && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                  {topics.flatMap((topic) =>
                    topic.images.slice(0, 3).map((img, i) => (
                      <button
                        key={`${topic.id}-${img.id}-${i}`}
                        onClick={() => openFullscreen(img, i, topic.images)}
                        className="gallery-image-card relative aspect-square rounded-xl overflow-hidden group focus:outline-none focus:ring-2 focus:ring-[#e53e3e]"
                        aria-label={`View ${img.name}`}>
                        <AppImage
                          src={img.thumbUrl}
                          alt={img.name}
                          fill
                          className="object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                          unoptimized
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-end justify-start p-2">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-heading font-600 text-white bg-black/50">
                            {topic.label}
                          </span>
                        </div>
                      </button>
                    ))
                  )}
                  {topics.every((t) => t.images.length === 0) && (
                    <div className="col-span-2 sm:col-span-4 flex flex-col items-center justify-center h-40 sm:h-48 text-[#8A9BB8] bg-[#0d1a30] rounded-2xl border border-[#e53e3e]/20">
                      <Icon name="PhotoIcon" size={40} className="mb-3 opacity-30" />
                      <p className="font-heading font-600 text-[#F0E6CC] text-base sm:text-lg mb-1">No images yet</p>
                      <p className="font-body text-xs sm:text-sm text-center px-4">Images will appear here once added from the admin panel.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Topic-specific view */}
              {activeTopic && (
                <>
                  {topicLoading ? (
                    <div className="flex items-center justify-center h-40 sm:h-48">
                      <div className="w-6 h-6 border-2 border-[#e53e3e]/30 border-t-[#e53e3e] rounded-full animate-spin" />
                      <span className="ml-3 text-sm text-[#8A9BB8] font-body">Loading images...</span>
                    </div>
                  ) : displayImages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-40 sm:h-48 text-[#8A9BB8] bg-[#0d1a30] rounded-2xl border border-[#e53e3e]/20">
                      <Icon name="PhotoIcon" size={40} className="mb-3 opacity-30" />
                      <p className="font-heading font-600 text-[#F0E6CC] text-base sm:text-lg mb-1">No images yet</p>
                      <p className="font-body text-xs sm:text-sm text-center px-4">Images will appear here once added from the admin panel.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                      {displayImages.map((img, i) => (
                        <button
                          key={`${img.id}-${i}`}
                          onClick={() => openFullscreen(img, i, displayImages)}
                          className="gallery-image-card relative aspect-square rounded-xl overflow-hidden group focus:outline-none focus:ring-2 focus:ring-[#e53e3e]"
                          aria-label={`View ${img.name}`}>
                          <AppImage
                            src={img.thumbUrl}
                            alt={img.name}
                            fill
                            className="object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                            unoptimized
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                            <Icon name="MagnifyingGlassPlusIcon" size={28} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Fullscreen Viewer */}
      {fullscreen && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4"
          onClick={() => setFullscreen(null)}>
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => navFullscreen(-1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              aria-label="Previous image">
              <Icon name="ChevronLeftIcon" size={20} />
            </button>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <AppImage
                src={fullscreen.src}
                alt={fullscreen.alt}
                width={1200}
                height={800}
                className="w-full h-auto max-h-[80vh] object-contain"
                unoptimized
              />
            </div>
            <button
              onClick={() => navFullscreen(1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              aria-label="Next image">
              <Icon name="ChevronRightIcon" size={20} />
            </button>
            <div className="flex items-center justify-between mt-4">
              <p className="font-body text-white/70 text-sm">{fullscreen.index + 1} / {fullscreen.images.length}</p>
              <button
                onClick={() => setFullscreen(null)}
                className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                aria-label="Close viewer">
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// ─────────────────────────────────────────────
// VIDEO SECTION
// ─────────────────────────────────────────────
const VideoSection: React.FC<{ videos: VideoItem[]; loading?: boolean }> = ({ videos, loading = false }) => {
  const [activeVideo, setActiveVideo] = useState<VideoItem | null>(null);

  // Debug: log what videos array looks like at render
  useEffect(() => {
    if (!loading) {
      console.log("[VideoSection] rendering", videos.length, "videos:", videos);
    }
  }, [videos, loading]);

  // Auto-select first video when videos load
  useEffect(() => {
    if (!loading && videos.length > 0 && activeVideo === null) {
      // Don't auto-open modal, just ensure grid shows
    }
  }, [videos, loading, activeVideo]);

  return (
    <section id="videos" className="py-14 sm:py-20 lg:py-28 bg-[#0a1525]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <div className="flex items-center justify-center gap-3 mb-4 reveal">
            <div className="h-px w-10 bg-[#e53e3e]" />
            <span className="accent-badge px-3 py-1 rounded-full">Videos</span>
            <div className="h-px w-10 bg-[#e53e3e]" />
          </div>
          <h2 className="font-heading font-800 text-3xl sm:text-4xl md:text-5xl text-[#F0E6CC] mb-4 reveal">
            School in <span className="text-[#e53e3e]">Motion</span>
          </h2>
          <p className="font-body text-[#8A9BB8] text-base sm:text-lg max-w-xl mx-auto reveal">
            Watch highlights from our events, celebrations, and everyday school life.
          </p>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-[#0d1a30] rounded-2xl overflow-hidden border border-[#e53e3e]/15 shadow-card animate-pulse">
                <div className="aspect-video bg-[#1e2e48]" />
                <div className="p-4">
                  <div className="h-4 bg-[#1e2e48] rounded w-3/4 mb-2" />
                  <div className="h-3 bg-[#1e2e48] rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : videos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 sm:py-16 text-center">
            <div className="w-16 h-16 bg-[#0d1a30] rounded-full flex items-center justify-center mb-4 border border-[#e53e3e]/20">
              <Icon name="VideoCameraIcon" size={28} className="text-[#e53e3e]" />
            </div>
            <p className="font-heading font-600 text-[#F0E6CC] text-lg mb-1">No videos yet</p>
            <p className="font-body text-[#8A9BB8] text-sm">Videos will appear here once added from the admin panel.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {videos.map((video, i) => (
                <button
                  key={video.id}
                  onClick={() => setActiveVideo(video)}
                  className="video-card text-left bg-[#0d1a30] rounded-2xl overflow-hidden border border-[#e53e3e]/15 shadow-card focus:outline-none focus:ring-2 focus:ring-[#e53e3e] reveal"
                  style={{ animationDelay: `${i * 0.08}s` }}
                  aria-label={`Play ${video.title}`}>
                  <div className="relative aspect-video overflow-hidden">
                    <AppImage
                      src={video.thumbnailUrl || "/assets/images/no_image.png"}
                      alt={`Thumbnail for ${video.title}`}
                      fill
                      className="object-cover"
                      loading="lazy"
                      unoptimized
                    />
                    <div className="absolute inset-0 bg-[#060e1e]/40 flex items-center justify-center">
                      <div className="play-btn-ring w-12 sm:w-14 h-12 sm:h-14 bg-gradient-to-br from-[#e53e3e] to-[#c53030] rounded-full flex items-center justify-center relative z-10">
                        <Icon name="PlayIcon" size={20} className="text-white ml-1" variant="solid" />
                      </div>
                    </div>
                    <div className="absolute top-2 sm:top-3 right-2 sm:right-3 bg-[#e53e3e] text-white text-[10px] font-heading font-700 px-2 py-0.5 rounded">YouTube</div>
                  </div>
                  <div className="p-3 sm:p-4">
                    <h4 className="font-heading font-600 text-sm text-[#F0E6CC] leading-snug">{video.title}</h4>
                    <p className="text-xs text-[#8A9BB8] mt-1 flex items-center gap-1">
                      <Icon name="PlayCircleIcon" size={13} className="text-[#e53e3e]" />Click to watch
                    </p>
                  </div>
                </button>
              ))}
          </div>
        )}
      </div>
      {activeVideo && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4"
          onClick={() => setActiveVideo(null)}>
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                src={`https://www.youtube.com/embed/${extractYouTubeId(activeVideo.youtubeUrl)}?autoplay=1`}
                title={activeVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
            <div className="flex items-center justify-between mt-4">
              <h3 className="font-heading font-600 text-white text-base">{activeVideo.title}</h3>
              <button
                onClick={() => setActiveVideo(null)}
                className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                aria-label="Close video">
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// ─────────────────────────────────────────────
// CONTACT SECTION
// ─────────────────────────────────────────────
const ContactSection: React.FC = () => {
  const schools = [
    {
      name: "Royal Nursery & Primary School",
      address: "10, Rajampettai Street, Thakkolam – 631 151",
      phone: "04177-246311",
      mobile: "7708000475",
      email: "royalnpschool@gmail.com",
      website: null,
      gradient: "from-[#e53e3e] to-[#c53030]",
    },
    {
      name: "Royal Matric. Hr. Sec. School",
      address: "Ananthapuram, Thakkolam",
      phone: "04177-246786",
      mobile: "99440 26850",
      email: "royalmatriculationschool@gmail.com",
      website: "www.royalmatrichrsecschool.com",
      gradient: "from-[#fc8181] to-[#e53e3e]",
    },
    {
      name: "Royal CBSE School",
      address: "10, Rajampettai Street, Thakkolam – 631 151",
      phone: "04177-246311",
      mobile: "7708000475",
      email: "royalnpschool@gmail.com",
      website: null,
      gradient: "from-[#e53e3e] to-[#c53030]",
    },
  ];

  return (
    <section id="contact" className="py-14 sm:py-20 lg:py-28 bg-[#060e1e]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <div className="flex items-center justify-center gap-3 mb-4 reveal">
            <div className="h-px w-10 bg-[#e53e3e]" />
            <span className="accent-badge px-3 py-1 rounded-full">Contact</span>
            <div className="h-px w-10 bg-[#e53e3e]" />
          </div>
          <h2 className="font-heading font-800 text-3xl sm:text-4xl md:text-5xl text-[#F0E6CC] mb-4 reveal">
            Find <span className="text-[#e53e3e]">Us</span>
          </h2>
          <p className="font-body text-[#8A9BB8] text-lg max-w-xl mx-auto reveal">
            Three schools, one legacy of excellence. Reach out to any of our campuses in Thakkolam.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {schools.map((school, idx) => (
            <div key={idx} className="bg-[#0d1a30] rounded-2xl border border-[#e53e3e]/20 card-hover reveal overflow-hidden">
              {/* School header */}
              <div className={`bg-gradient-to-r ${school.gradient} p-4 sm:p-5`}>
                <h3 className="font-heading font-800 text-white text-sm sm:text-base leading-snug">{school.name}</h3>
              </div>
              {/* Details */}
              <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
                {/* Address */}
                <div className="flex gap-3 items-start">
                  <div className="w-8 sm:w-9 h-8 sm:h-9 bg-[#e53e3e]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="MapPinIcon" size={16} className="text-[#e53e3e]" />
                  </div>
                  <p className="font-body text-[#8A9BB8] text-xs sm:text-sm leading-relaxed">{school.address}</p>
                </div>
                {/* Phone */}
                <div className="flex gap-3 items-center">
                  <div className="w-8 sm:w-9 h-8 sm:h-9 bg-[#e53e3e]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="PhoneIcon" size={16} className="text-[#e53e3e]" />
                  </div>
                  <div>
                    <a href={`tel:${school.phone}`} className="font-body text-[#e53e3e] text-xs sm:text-sm font-600 hover:text-[#fc8181] transition-colors block">{school.phone}</a>
                    <a href={`tel:${school.mobile}`} className="font-body text-[#8A9BB8] text-xs sm:text-sm hover:text-[#e53e3e] transition-colors block">{school.mobile}</a>
                  </div>
                </div>
                {/* Email */}
                <div className="flex gap-3 items-center">
                  <div className="w-8 sm:w-9 h-8 sm:h-9 bg-[#e53e3e]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="EnvelopeIcon" size={16} className="text-[#e53e3e]" />
                  </div>
                  <a href={`mailto:${school.email}`} className="font-body text-[#e53e3e] text-xs sm:text-sm font-600 hover:text-[#fc8181] transition-colors break-all">{school.email}</a>
                </div>
                {/* Website (if available) */}
                {school.website && (
                  <div className="flex gap-3 items-center">
                    <div className="w-8 sm:w-9 h-8 sm:h-9 bg-[#e53e3e]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="GlobeAltIcon" size={16} className="text-[#e53e3e]" />
                    </div>
                    <a href={`https://${school.website}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-white/80 text-xs font-heading font-700 hover:text-white">
                      <Icon name="ArrowTopRightOnSquareIcon" size={14} />Get Directions
                    </a>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Google Map */}
        <div className="mt-8 sm:mt-10 rounded-2xl overflow-hidden border border-[#e53e3e]/20 reveal" style={{ height: "clamp(240px, 50vw, 400px)" }}>
          <iframe
            title="Thakkolam Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.0!2d79.73621607892949!3d13.027588863919917!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTMCMDEnNDIuMyJOIDc5wrA0NCcxMC40IkU!5e0!3m2!1sen!2sin!4v1680000000000!5m2!1sen!2sin"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────
// SCROLL REVEAL HOOK
// ─────────────────────────────────────────────
const useScrollReveal = () => {
  useEffect(() => {
    const observe = () => {
      const elements = document.querySelectorAll(".reveal:not(.visible)");
      elements.forEach((el) => observer.observe(el));
    };
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((entry) => { if (entry.isIntersecting) entry.target.classList.add("visible"); }); },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    observe();
    // Re-observe when new elements are added to the DOM (e.g. async video cards)
    const mutationObserver = new MutationObserver(observe);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, []);
};

// ─────────────────────────────────────────────
// HOME PAGE
// ─────────────────────────────────────────────
export default function HomePage() {
  useScrollReveal();
  const [liveVideos, setLiveVideos] = useState<VideoItem[]>([]);
  const [videosLoaded, setVideosLoaded] = useState(false);
  const [videosLoading, setVideosLoading] = useState(true);

  useEffect(() => {
    const loadVideos = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from("school_videos")
          .select("id, title, youtube_url")
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: true });
        console.log("[VideoFetch] data:", data, "error:", error);
        if (error) {
          console.error("[VideoFetch] Supabase error:", error.message, error.code, error.details);
        }
        if (data && data.length > 0) {
          setLiveVideos(data.map(mapVideoRow));
          setVideosLoaded(true);
          setVideosLoading(false);
        }
      } catch (err) {
        console.error("[VideoFetch] Exception:", err);
      } finally {
        setVideosLoaded(true);
        setVideosLoading(false);
      }
    };
    loadVideos();
  }, []);

  // Real-time: update video list when admin adds/removes videos
  useEffect(() => {
    const supabase = createClient();
    const channel = supabase
      .channel("school_videos_realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "school_videos" },
        async () => {
          const { data, error } = await supabase
            .from("school_videos")
            .select("id, title, youtube_url")
            .order("sort_order", { ascending: true })
            .order("created_at", { ascending: true });
          console.log("[VideoRealtime] data:", data, "error:", error);
          if (error) {
            console.error("[VideoRealtime] error:", error.message);
          }
          if (data !== null) {
            setLiveVideos(data.map(mapVideoRow));
            setVideosLoaded(true);
            setVideosLoading(false);
          }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // Show DB videos only — no hardcoded fallback
  const videosToShow = liveVideos;

  return (
    <>
      <PWAInstallPrompt />
      <main className="min-h-screen bg-[#060e1e]">
        <Header />
        <HeroSlider config={SLIDER_CONFIG} />
        <div className="section-divider" />
        <AboutSection />
        <div className="section-divider" />
        <AcademicsSection />
        <div className="section-divider" />
        <GallerySection />
        <div className="section-divider" />
        <VideoSection videos={videosToShow} loading={videosLoading} />
        <div className="section-divider" />
        <ContactSection />
        <Footer />
      </main>
    </>
  );
}